import { z } from 'zod/v3';
import { createPaginationSchema, zIsoDate } from '../../common/utils';
import { zIsoDateTime } from '../../common/utils/schema/zIsoDateTime';
import { SampleCommentValidFormDataSchema } from './SampleCommentSchema';
import { zDataType } from '../../common/utils/schema/zDataType';
import { ChangedBySchema } from '../../user';

export const ColumnSchema = z.object({
  code: z.string(),
  id: z.string().nullish(),
  name: z.string(),
  type: z.enum(['string', 'number']),
  decimals: z.number().nullish(),
  required: z.boolean().nullish(),
  hidden: z.boolean().nullish(),
  dataSubjectAttribute: z.boolean().nullish(),
  allCategories: z.boolean().nullish(),
  classifierCode: z.string().nullish(),
  codeFormat: z.string().nullish(),
  dataType: zDataType.nullish(),
});

export const CategoryNameSchema = z.object({
  code: z.string(),
  name: z.string(),
  categoryType: z
    .enum(['category', 'category_group', 'combined_group'])
    .nullish(),
  validFrom: zIsoDate().nullish(),
  validTo: zIsoDate().nullish(),
});

export const CategoryNameSchemasSchema = z.record(
  z.string(),
  CategoryNameSchema,
);
export const SubjectsSchema = z.record(z.string(), z.string());

export const DataRowSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.null()]),
);
export const LabelsSchema = z.record(z.string(), CategoryNameSchemasSchema);

export const InputDataSchema = z.object({
  columns: z.array(ColumnSchema),
  labels: LabelsSchema.nullable(),
  rows: z.array(DataRowSchema),
});

export const InputDataPageSchema = createPaginationSchema(DataRowSchema).extend(
  {
    subjectNameMappings: z.record(z.string(), z.string()),
  },
);

export const InputDataInfoSchema = z.object({
  columns: z.array(ColumnSchema),
  labels: LabelsSchema.nullable(),
  versionStatusId: z.number().nullable(),
  calculationTime: zIsoDateTime().nullable(),
});

export const ConfidentialDescriptionSchema = z.object({
  id: z.number(),
  hasAccess: z.boolean().nullable(),
});
export const ConfidentialDescriptionArraySchema = z.array(
  ConfidentialDescriptionSchema,
);

export const InputDataValidFormDataSchema = z.object({
  rows: z.array(z.any()),
  comments: z.array(SampleCommentValidFormDataSchema).optional(),
});

export const InputDataMutationDtoSchema = z.object({
  columns: z.array(z.string()),
  rows: DataRowSchema.array(),
});

export const InputDataUploadInfoMetadataSchema = z.object({
  dataObjectDescriptionId: z.number(),
  dataObjectDescriptionCode: z.string(),
  dataObjectDescriptionName: z.string(),
  dataObjectGroupId: z.number().nullable(),
  dataObjectGroupCode: z.string().nullable(),
  dataObjectGroupName: z.string().nullable(),
  valueDate: zIsoDate(),
  subjectsCount: z.number(),
});

export const InputDataUploadInfoSchema = z.object({
  metadata: z.array(InputDataUploadInfoMetadataSchema),
  filePath: z.string(),
});

export const InputDataActiveInputSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  versionId: z.number(),
  latestVersionCreatedAt: z.string().nullable(),
  latestVersionStatusId: z.number().nullable(),
});

export const InputDataActiveInfoSchema = z.object({
  groupCode: z.string(),
  groupName: z.string(),
  valueDate: zIsoDate(),
  frequencyId: z.number(),
  inputs: z.array(InputDataActiveInputSchema),
});

export const InputDataOutputsSchema = z.object({
  outputDataObjectId: z.number(),
  outputDataObjectValueDate: zIsoDate(),
  outputDescriptionId: z.number(),
  outputDescriptionCode: z.string(),
  outputDescriptionName: z.string(),
});
export const InputDataOutputsArraySchema = z.array(InputDataOutputsSchema);

export const OutputValueDateSchema = z.object({
  resultDoId: z.number(),
  resultDoValueDate: zIsoDate(),
});
export const OutputValueDateArraySchema = z.array(OutputValueDateSchema);

export const OutputSubmittedDataSchema = z.object({
  id: z.number(),
  statusId: z.number().nullable(),
  statusCode: z.string().nullable(),
  statusName: z.string().nullable(),
  dataModifiedAt: zIsoDateTime().nullable(),
  dataModifiedBy: ChangedBySchema.nullable(),
  versionNumber: z.number().nullable(),
  minorNumber: z.number().nullable(),
});

export const OutputVersionInputSourceSchema = z.object({
  code: z.string(),
  name: z.string(),
  statusId: z.number(),
  statusCode: z.string(),
  statusName: z.string(),
  versionCreatedAt: zIsoDateTime(),
  calculationTime: zIsoDateTime().nullable(),
  submittedData: OutputSubmittedDataSchema,
});

export const OutputVersionSchema = z.object({
  outputDovId: z.number(),
  outputVersionNumber: z.number(),
  outputMinorNumber: z.number().nullable(),
  outputStatusId: z.number(),
  outputStatusCode: z.string().nullable(),
  outputStatusName: z.string().nullable(),
  outputValueDate: zIsoDate(),
  active: z.boolean(),
  calculationTime: zIsoDateTime().nullable(),
  inputSources: z.array(OutputVersionInputSourceSchema),
});
export const OutputVersionArraySchema = z.array(OutputVersionSchema);

export const OutputSnapshotVersionSchema = z.object({
  output: InputDataOutputsSchema,
  outputDate: OutputValueDateSchema,
  outputVersion: OutputVersionSchema,
});
